import {
  type Notification,
  NotificationType,
} from "@/features/notification/types";
import { cn } from "@/utils/ui";
import { BellIcon } from "lucide-react";
import { useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

export function NotificationDropdown({ className }: { className?: string }) {
  // Sample notifications data - in a real app, this would come from an API
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: "1",
      type: NotificationType.System,
      title: "System Update",
      content: "Your account has been verified successfully.",
      isRead: false,
      link: "/account",
      createdAt: new Date(Date.now() - 1000 * 60 * 5), // 5 minutes ago
    },
    {
      id: "2",
      type: NotificationType.Message,
      title: "New Message",
      content: "Sarah sent you a new message.",
      isRead: true,
      link: "/messages/123",
      createdAt: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
    },
    {
      id: "3",
      type: NotificationType.Mention,
      title: "New Mention",
      content: "Alex mentioned you in a comment.",
      isRead: false,
      link: "/artworks/456",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    },
    {
      id: "4",
      type: NotificationType.System,
      title: "New Feature",
      content: "We've added a new gallery feature.",
      isRead: false,
      link: "/gallery",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    },
    {
      id: "5",
      type: NotificationType.Mention,
      title: "New Mention",
      content: "Taylor mentioned you in an artwork.",
      isRead: true,
      link: "/artworks/789",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
    },
  ]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markAsRead = (id: string) => {
    setNotifications(
      notifications.map((notification) =>
        notification.id === id
          ? { ...notification, isRead: true }
          : notification,
      ),
    );
  };

  const markAllAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({ ...notification, isRead: true })),
    );
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) {
      return "Just now";
    }
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getNotificationsByType = (type: NotificationType | "all") => {
    return type === "all"
      ? notifications
      : notifications.filter((notification) => notification.type === type);
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case "message":
        return "💬";
      case "mention":
        return "📣";
      case "system":
        return "🔔";
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          intent="plain"
          size="icon"
          className={cn("text-gray-fg-secondary relative", className)}
        >
          <BellIcon />
          {unreadCount > 0 && (
            <Badge
              size="sm"
              className="absolute top-0 right-0 rounded-full group-hover:bg-accent-solid-hover"
            >
              {unreadCount}
            </Badge>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between border-b p-3">
          <h3 className="font-medium">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              intent="ghost"
              flavor="gray"
              alpha
              size="xs"
              onClick={markAllAsRead}
            >
              Mark all as read
            </Button>
          )}
        </div>
        <Tabs defaultValue="all" activeStyle="border" flavor="graya">
          <TabsList className="justify-start border-b text-sm gap-0">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="message">Messages</TabsTrigger>
            <TabsTrigger value="mention">Mentions</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
          </TabsList>

          {["all", "message", "mention", "system"].map((type) => (
            <TabsContent key={type} value={type}>
              <ScrollArea className="h-[300px]" type="auto">
                {getNotificationsByType(type as NotificationType | "all")
                  .length > 0 ? (
                  <div className="divide-y">
                    {getNotificationsByType(
                      type as NotificationType | "all",
                    ).map((notification) => (
                      <div
                        key={notification.id}
                        className={cn(
                          "flex items-start gap-3 p-3 hover:bg-graya-ui transition-colors cursor-pointer",
                          !notification.isRead && "bg-graya-ui/50",
                        )}
                        onClick={() => markAsRead(notification.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            markAsRead(notification.id);
                          }
                        }}
                      >
                        <div className="flex size-8 items-center justify-center rounded-full bg-graya-subtle-bg">
                          {getTypeIcon(notification.type)}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p
                            className={cn(
                              "text-sm",
                              !notification.isRead && "font-medium",
                            )}
                          >
                            {notification.content}
                          </p>
                          <p className="text-xs text-gray-fg-secondary">
                            {formatTimestamp(notification.createdAt)}
                          </p>
                        </div>
                        {!notification.isRead && (
                          <Badge
                            size="sm"
                            className="rounded-full size-2 px-0"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex h-32 items-center justify-center text-sm text-gray-fg-secondary">
                    No notifications to display
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          ))}
        </Tabs>
        <div className="border-t p-2">
          <Button
            intent="outline"
            flavor="gray"
            size="md"
            alpha
            className="w-full"
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
